package com.eladfinish.bible.contest.screens.mikud.mvi

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import androidx.compose.ui.text.AnnotatedString
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.screens.mikud.model.BookClickData
import com.eladfinish.bible.contest.screens.mikud.model.BookState
import com.eladfinish.bible.contest.screens.mikud.model.ChapterClickData
import com.eladfinish.bible.contest.screens.mikud.model.MikudLevelInfo
import com.eladfinish.bible.contest.screens.mikud.model.MikudSummaryModel
import com.eladfinish.bible.contest.screens.mikud.model.MikudType
import com.eladfinish.bible.contest.screens.mikud.model.MikudTypeData
import com.eladfinish.bible.contest.screens.mikud.model.RemoteMikudMenuItem
import com.eladfinish.bible.contest.ui.menus.actions.MikudToolbarAction
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf

@Immutable
data class MikudState(
    val isSkipReadMeChecked: Boolean,
    val isLoading: Boolean,
    val dialogState: DialogState,
    val isPremium: Boolean = false,
    val mikudState: ImmutableList<BookState> = persistentListOf(),
    val selectedBookIndex: Int?,
    val hebrewGender: HebrewGender = HebrewGender.MALE,
    val isDebugMode: Boolean,
) : StateKt {

    @Stable
    sealed class DialogState {
        data class Help(val isDontShowAgainChecked: Boolean) : DialogState()
        data class MikudSummary(val summary: MikudSummaryModel) : DialogState()

        data class LoadMikudMenu(
            val title: StringDesc,
            val presets: ImmutableList<MikudTypeData>,
            val savedItems: ImmutableList<String>,
        ) : DialogState()

        data class MikudLevels(
            val mikudType: MikudType,
            val info: MikudLevelInfo,
            val title: String,
            val annotatedText: AnnotatedString,
        ) : DialogState()

        data class RemoteMikudMenu(
            val title: String,
            val items: ImmutableList<RemoteMikudMenuItem>,
        ) : DialogState()

        data class MikudNameEntry(val title: StringDesc, val initialValue: String = "") : DialogState()
        data class MikudRenameEntry(val title: StringDesc, val initialValue: String = "") : DialogState()
        data class ConfirmDeleteSavedMikud(val name: String) : DialogState()
        data class PremiumRequired(val message: String) : DialogState()
        data object None : DialogState()
    }
}

sealed class MikudAction : ActionKt {
    data class ToolbarClicked(val toolbarAction: MikudToolbarAction) : MikudAction()
    data class ToggleHelp(val shouldShowNextTime: Boolean) : MikudAction()
    data class BookToggled(val bookClickData: BookClickData) : MikudAction()
    data class ChapterToggled(val chapterClickData: ChapterClickData) : MikudAction()
    data class PresetSelected(val type: MikudType) : MikudAction()
    data class MikudLevelClicked(val payload: String) : MikudAction()
    data class SaveMikudWithName(val name: String) : MikudAction()
    data class RenameMikud(val oldName: String, val newName: String) : MikudAction()
    data class LoadMikudByIndex(val index: Int) : MikudAction()
    data class LoadMikudByName(val name: String) : MikudAction()
    data class DeleteSavedMikud(val name: String) : MikudAction()
    data class RequestDeleteSavedMikud(val name: String) : MikudAction()
    data class ConfirmDeleteSavedMikud(val name: String) : MikudAction()
    data class RenameSavedMikud(val oldName: String) : MikudAction()
    data class MikudLoadedFromServer(val response: String) : MikudAction()
    data class RemoteMikudClicked(val itemIndex: Int) : MikudAction()
    data class SyllabusLoaded(val mikudType: MikudType, val info: MikudLevelInfo) : MikudAction()
    data class RefreshMikudLevels(val mikudType: MikudType) : MikudAction()
    data object CancelDeleteSavedMikud : MikudAction()
    data object LoadMikudClicked : MikudAction()
    data object SaveMikudClicked : MikudAction()
    data object PurchasePremiumClicked : MikudAction()
    data object HideDialog : MikudAction()
    data object BackClicked : MikudAction()
}

sealed class MikudEffect : EffectKt {
    data class ShowToast(val message: String) : MikudEffect()
    data class ShowToastRes(val resource: StringResource) : MikudEffect()
    data object NavigateBack : MikudEffect()
}
